import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { formatMoney, formatDate, formatDateTime } from '../shared/formatting';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { AdminCreateCard } from '../shared/AdminCreateCard';
import { DateInput } from '../shared/DateInput';

export type FinAdminSection = '122' | '123' | '124';

type FinFacilityRow = {
    id: number;
    company_id: number;
    company_code: string;
    counterparty_id: number | null;
    counterparty_name: string | null;
    title: string;
    reference_code: string | null;
    principal_amount: number;
    currency_code: string;
    start_date: string | null;
    maturity_date: string | null;
    docdoc_contract_url: string | null;
    notes: string | null;
    status: string;
    archived_at: string | null;
    monthly_installment?: number | null;
    annual_interest_rate_pct?: number | null;
    accrual_basis?: string | null;
    daily_interest_rate_pct?: number | null;
    created_at?: string | null;
    updated_at?: string | null;
    current_balance?: number | null;
};

type AdjustmentRow = {
    id: number;
    company_id: number;
    effective_date: string;
    amount: number;
    note: string | null;
    status: string;
    archived_at: string | null;
};

type Module5ReconciliationRow = {
    kind: string;
    overlay_type: string;
    overlay_ref: string;
    id: number;
    company_id: number;
    company_code: string;
    counterparty_id: number | null;
    counterparty_name: string | null;
    account_code: string | null;
    account_name: string | null;
    metric: string;
    pd_ipo_amount: number;
    trial_balance_amount: number;
    difference: number;
    reconciliation_status: string;
    system_note: string | null;
    display_title: string | null;
    admin_comment: string | null;
    opening_balance_delta: number | null;
};

type Module5TbRow = {
    kind: string;
    overlay_type: string;
    overlay_ref: string;
    company_id: number;
    company_code: string;
    account_code: string;
    account_name: string | null;
    signed_opening: number;
    instrument: string;
    display_title: string | null;
    admin_comment: string | null;
    opening_balance_delta: number | null;
};

type BankAccountAdminRow = {
    kind: string;
    overlay_type: string;
    overlay_ref: string;
    id: number;
    company_id: number;
    company_code: string;
    iban: string;
    bank_name: string | null;
    account_label: string | null;
    currency_code: string;
    opening_balance?: number;
    opening_date?: string | null;
    opening_source?: string | null;
    movements_n?: number;
    movements_debit?: number;
    movements_credit?: number;
    movements_net?: number;
    last_movement_date?: string | null;
    current_balance?: number;
    is_active: boolean;
    display_title: string | null;
    admin_comment: string | null;
    opening_balance_delta: number | null;
};

type OverlayTarget = { overlay_type: string; overlay_ref: string };

type LoanMovementGroupRow = {
    kind: 'loan_movement_group';
    overlay_type: 'loan_movement_group';
    overlay_ref: string;
    company_id: number;
    company_code: string;
    counterparty_id: number | null;
    counterparty_name: string | null;
    n: number;
    inflow: number;
    outflow: number;
    net: number;
    opening_principal: number;
    opening_interest: number;
    opening_total: number;
    current_balance: number;
    recon_notes: string[];
    first_movement: string | null;
    last_movement: string | null;
    display_title: string | null;
    admin_comment: string | null;
    opening_balance_delta: number | null;
};

type IndexPayload = {
    segment: string;
    rows: FinFacilityRow[];
    module5_reconciliations: Module5ReconciliationRow[];
    module5_tb_positions: Module5TbRow[];
    bank_accounts: BankAccountAdminRow[];
    loan_movement_groups?: LoanMovementGroupRow[];
    companies: Array<{ id: number; code: string; name: string }>;
    counterparties: Array<{ id: number; name: string }>;
    docdoc: { enabled: boolean; base_url: string | null; note: string };
};

function segmentFromSection(section: FinAdminSection): string {
    if (section === '122') {
        return 'bank-credits';
    }
    if (section === '123') {
        return 'leasing-contracts';
    }

    return 'loan-book';
}

/** Jasni naslovi obrasca — „Novi zapis“ zamijenjen konkretnim tipom instrumenta. */
function finFacilityFormCopy(section: FinAdminSection, locale: 'hr' | 'en'): {
    newTitle: string;
    newHint: string;
    editTitle: (id: number) => string;
} {
    if (section === '122') {
        return locale === 'en'
            ? {
                  newTitle: 'New bank credit',
                  newHint: 'Register a bank loan or credit facility for the selected company (phase F). Set counterparty to the bank when known. Feeds Bank Ready Pack and the Loans module.',
                  editTitle: (id: number) => `Edit bank credit #${id}`,
              }
            : {
                  newTitle: 'Novi bankovni kredit',
                  newHint: 'Evidencija kredita ili kreditne linije prema banci za odabranu tvrtku (registar faze F). Protustranka: banka, ako je u sustavu. Koristi se u Bank Ready Packu i u modulu Pozajmice.',
                  editTitle: (id: number) => `Uredi bankovni kredit #${id}`,
              };
    }
    if (section === '123') {
        return locale === 'en'
            ? {
                  newTitle: 'New leasing contract',
                  newHint: 'Register a leasing liability for the company (phase F). Counterparty is usually the leasing house. Feeds Bank Ready Pack and the Loans module.',
                  editTitle: (id: number) => `Edit leasing contract #${id}`,
              }
            : {
                  newTitle: 'Novi leasing ugovor',
                  newHint: 'Evidencija leasing obveze za tvrtku (registar faze F). Protustranka: leasing kuća. Koristi se u Bank Ready Packu i u modulu Pozajmice.',
                  editTitle: (id: number) => `Uredi leasing ugovor #${id}`,
              };
    }

    return locale === 'en'
        ? {
              newTitle: 'New loan book entry',
              newHint: 'Internal or related-party loan record (module 124). Used in the Loans module; you can exclude these rows from Bank Ready Pack export (“Without loan book (124)”).',
              editTitle: (id: number) => `Edit loan book entry #${id}`,
          }
        : {
              newTitle: 'Novi zapis u knjizi pozajmica',
              newHint: 'Zajam ili obveza prema povezanoj strani (modul 124). Za Bank Ready Pack možete izvoz raditi i bez ovih redaka (opcija „Bez knjige pozajmica“).',
              editTitle: (id: number) => `Uredi zapis knjige pozajmica #${id}`,
          };
}

export function AdminFinFacilitiesSection({
    token,
    locale,
    reloadTick,
    adminSection,
}: {
    token: string;
    locale: 'hr' | 'en';
    reloadTick: number;
    adminSection: FinAdminSection;
}) {
    const segment = useMemo(() => segmentFromSection(adminSection), [adminSection]);
    const def = adminSectionDefinition(adminSection);
    const title = def ? adminSectionNavLabel(def, locale) : adminSection;

    const [payload, setPayload] = useState<IndexPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
    const [companyFilter, setCompanyFilter] = useState<string>('');
    const [tableSearch, setTableSearch] = useState('');
    const [openActionMenuId, setOpenActionMenuId] = useState<number | string | null>(null);

    // Zatvori akcije dropdown na klik izvan
    useEffect(() => {
        if (openActionMenuId === null) return;
        const handler = (ev: MouseEvent) => {
            const target = ev.target as HTMLElement;
            if (!target.closest('.row-actions-menu')) {
                setOpenActionMenuId(null);
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [openActionMenuId]);

    const [nCompany, setNCompany] = useState('');
    const [nCounterparty, setNCounterparty] = useState('');
    const [nTitle, setNTitle] = useState('');
    const [nRef, setNRef] = useState('');
    const [nPrincipal, setNPrincipal] = useState('');
    const [nCurrency, setNCurrency] = useState('EUR');
    const [nStart, setNStart] = useState('');
    const [nMaturity, setNMaturity] = useState('');
    const [nDocdoc, setNDocdoc] = useState('');
    const [nNotes, setNNotes] = useState('');
    const [nMonthly, setNMonthly] = useState('');
    const [nAnnualPct, setNAnnualPct] = useState('');

    const [editId, setEditId] = useState<number | null>(null);
    const [eCompany, setECompany] = useState('');
    const [eCounterparty, setECounterparty] = useState('');
    const [eTitle, setETitle] = useState('');
    const [eRef, setERef] = useState('');
    const [ePrincipal, setEPrincipal] = useState('');
    const [eCurrency, setECurrency] = useState('EUR');
    const [eStart, setEStart] = useState('');
    const [eMaturity, setEMaturity] = useState('');
    const [eDocdoc, setEDocdoc] = useState('');
    const [eNotes, setENotes] = useState('');
    const [eMonthly, setEMonthly] = useState('');
    const [eAnnualPct, setEAnnualPct] = useState('');

    const [adjFacilityId, setAdjFacilityId] = useState<number | null>(null);
    const [adjRows, setAdjRows] = useState<AdjustmentRow[]>([]);
    const [adjLoading, setAdjLoading] = useState(false);
    const [adjError, setAdjError] = useState('');
    const [nAdjDate, setNAdjDate] = useState('');
    const [nAdjAmount, setNAdjAmount] = useState('');
    const [nAdjNote, setNAdjNote] = useState('');

    const [adjOverlay, setAdjOverlay] = useState<OverlayTarget | null>(null);
    const [overlayAdjRows, setOverlayAdjRows] = useState<AdjustmentRow[]>([]);
    const [overlayAdjLoading, setOverlayAdjLoading] = useState(false);
    const [overlayAdjError, setOverlayAdjError] = useState('');
    const [nOverlayAdjDate, setNOverlayAdjDate] = useState('');
    const [nOverlayAdjAmount, setNOverlayAdjAmount] = useState('');
    const [nOverlayAdjNote, setNOverlayAdjNote] = useState('');

    const [enrichTarget, setEnrichTarget] = useState<OverlayTarget | null>(null);
    const [oTitle, setOTitle] = useState('');
    const [oComment, setOComment] = useState('');
    const [oOpeningDelta, setOOpeningDelta] = useState('');

    type ScheduleRow = { due_date: string; amount: number; kind: string; label: string };
    const [scheduleForId, setScheduleForId] = useState<number | null>(null);
    const [scheduleRows, setScheduleRows] = useState<ScheduleRow[]>([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const q = new URLSearchParams();
            q.set('status', statusFilter);
            const res = await api<IndexPayload>(`/admin/fin-facilities/${segment}?${q.toString()}`, token);
            setPayload({
                ...res,
                module5_reconciliations: res.module5_reconciliations ?? [],
                module5_tb_positions: res.module5_tb_positions ?? [],
                bank_accounts: res.bank_accounts ?? [],
                loan_movement_groups: res.loan_movement_groups ?? [],
            });
            setNCompany((c) => c || String(res.companies[0]?.id ?? ''));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [token, segment, statusFilter]);

    useEffect(() => {
        void load();
    }, [load, reloadTick]);

    const loadAdjustments = useCallback(
        async (facilityId: number) => {
            setAdjLoading(true);
            setAdjError('');
            try {
                const res = await api<{ rows: AdjustmentRow[] }>(
                    `/admin/fin-facilities/${segment}/${facilityId}/adjustments`,
                    token,
                );
                setAdjRows(res.rows);
            } catch (err) {
                setAdjError(err instanceof Error ? err.message : 'API');
                setAdjRows([]);
            } finally {
                setAdjLoading(false);
            }
        },
        [token, segment],
    );

    const loadSchedule = useCallback(
        async (facilityId: number) => {
            setScheduleLoading(true);
            setScheduleForId(facilityId);
            setScheduleRows([]);
            try {
                const res = await api<{ rows: ScheduleRow[] }>(
                    `/admin/fin-facilities/${segment}/${facilityId}/schedule`,
                    token,
                );
                setScheduleRows(res.rows ?? []);
            } catch {
                setScheduleRows([]);
            } finally {
                setScheduleLoading(false);
            }
        },
        [token, segment],
    );

    useEffect(() => {
        if (adjFacilityId === null) {
            setAdjRows([]);
            return;
        }
        void loadAdjustments(adjFacilityId);
    }, [adjFacilityId, loadAdjustments, reloadTick]);

    const loadOverlayAdjustments = useCallback(
        async (target: OverlayTarget) => {
            setOverlayAdjLoading(true);
            setOverlayAdjError('');
            try {
                const q = new URLSearchParams();
                q.set('overlay_type', target.overlay_type);
                q.set('overlay_ref', target.overlay_ref);
                const res = await api<{ rows: AdjustmentRow[] }>(
                    `/admin/fin-facilities/${segment}/overlay-adjustments?${q.toString()}`,
                    token,
                );
                setOverlayAdjRows(res.rows);
            } catch (err) {
                setOverlayAdjError(err instanceof Error ? err.message : 'API');
                setOverlayAdjRows([]);
            } finally {
                setOverlayAdjLoading(false);
            }
        },
        [token, segment],
    );

    useEffect(() => {
        if (adjOverlay === null) {
            setOverlayAdjRows([]);
            return;
        }
        void loadOverlayAdjustments(adjOverlay);
    }, [adjOverlay, loadOverlayAdjustments, reloadTick]);

    useEffect(() => {
        if (!enrichTarget || !payload) {
            return;
        }
        const fromRec = payload.module5_reconciliations.find(
            (x) => x.overlay_type === enrichTarget.overlay_type && x.overlay_ref === enrichTarget.overlay_ref,
        );
        const fromTb = payload.module5_tb_positions.find(
            (x) => x.overlay_type === enrichTarget.overlay_type && x.overlay_ref === enrichTarget.overlay_ref,
        );
        const fromBank = payload.bank_accounts.find(
            (x) => x.overlay_type === enrichTarget.overlay_type && x.overlay_ref === enrichTarget.overlay_ref,
        );
        const hit = fromRec ?? fromTb ?? fromBank;
        if (hit) {
            setOTitle(hit.display_title ?? '');
            setOComment(hit.admin_comment ?? '');
            setOOpeningDelta(hit.opening_balance_delta != null ? String(hit.opening_balance_delta) : '');
        }
    }, [enrichTarget, payload]);

    function startEdit(row: FinFacilityRow) {
        setEditId(row.id);
        setECompany(String(row.company_id));
        setECounterparty(row.counterparty_id ? String(row.counterparty_id) : '');
        setETitle(row.title);
        setERef(row.reference_code ?? '');
        setEPrincipal(String(row.principal_amount));
        setECurrency(row.currency_code || 'EUR');
        setEStart(row.start_date ? row.start_date.slice(0, 10) : '');
        setEMaturity(row.maturity_date ? row.maturity_date.slice(0, 10) : '');
        setEDocdoc(row.docdoc_contract_url ?? '');
        setENotes(row.notes ?? '');
        setEMonthly(row.monthly_installment != null ? String(row.monthly_installment) : '');
        setEAnnualPct(row.annual_interest_rate_pct != null ? String(row.annual_interest_rate_pct) : '');
    }

    async function submitNew(e: FormEvent) {
        e.preventDefault();
        if (!nCompany || !nTitle || !nPrincipal) {
            return;
        }
        setError('');
        const body: Record<string, unknown> = {
            company_id: Number(nCompany),
            counterparty_id: nCounterparty ? Number(nCounterparty) : null,
            title: nTitle,
            reference_code: nRef || null,
            principal_amount: Number(nPrincipal),
            currency_code: nCurrency || 'EUR',
            start_date: nStart || null,
            maturity_date: nMaturity || null,
            docdoc_contract_url: nDocdoc || null,
            notes: nNotes || null,
        };
        if (adminSection === '123' || adminSection === '122') {
            body.monthly_installment = nMonthly ? Number(nMonthly) : null;
        }
        if (adminSection === '124') {
            body.annual_interest_rate_pct = nAnnualPct ? Number(nAnnualPct) : null;
            body.accrual_basis = 'year_365';
        }
        try {
            await mutate(`/admin/fin-facilities/${segment}`, token, 'POST', body);
            setNTitle('');
            setNRef('');
            setNPrincipal('');
            setNStart('');
            setNMaturity('');
            setNDocdoc('');
            setNNotes('');
            setNMonthly('');
            setNAnnualPct('');
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function saveEdit(e: FormEvent) {
        e.preventDefault();
        if (!editId || !eCompany || !eTitle || !ePrincipal) {
            return;
        }
        setError('');
        const body: Record<string, unknown> = {
            company_id: Number(eCompany),
            counterparty_id: eCounterparty ? Number(eCounterparty) : null,
            title: eTitle,
            reference_code: eRef || null,
            principal_amount: Number(ePrincipal),
            currency_code: eCurrency || 'EUR',
            start_date: eStart || null,
            maturity_date: eMaturity || null,
            docdoc_contract_url: eDocdoc || null,
            notes: eNotes || null,
        };
        if (adminSection === '123' || adminSection === '122') {
            body.monthly_installment = eMonthly ? Number(eMonthly) : null;
        }
        if (adminSection === '124') {
            body.annual_interest_rate_pct = eAnnualPct ? Number(eAnnualPct) : null;
            body.accrual_basis = 'year_365';
        }
        try {
            await mutate(`/admin/fin-facilities/${segment}/${editId}`, token, 'PATCH', body);
            setEditId(null);
            await load();
            if (adjFacilityId === editId) {
                void loadAdjustments(editId);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function archiveRow(id: number) {
        setError('');
        try {
            await mutate(`/admin/fin-facilities/${segment}/${id}/archive`, token, 'PATCH', {});
            if (adjFacilityId === id) {
                setAdjFacilityId(null);
            }
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function deleteRow(id: number) {
        if (!window.confirm(locale === 'en' ? 'Soft-delete this row?' : 'Obrisati zapis (soft delete)?')) {
            return;
        }
        setError('');
        try {
            await mutate(`/admin/fin-facilities/${segment}/${id}`, token, 'DELETE', {});
            if (adjFacilityId === id) {
                setAdjFacilityId(null);
            }
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function submitAdjustment(e: FormEvent) {
        e.preventDefault();
        if (!adjFacilityId || !nAdjDate || nAdjAmount === '') {
            return;
        }
        setAdjError('');
        try {
            await mutate(`/admin/fin-facilities/${segment}/${adjFacilityId}/adjustments`, token, 'POST', {
                effective_date: nAdjDate,
                amount: Number(nAdjAmount),
                note: nAdjNote || null,
            });
            setNAdjAmount('');
            setNAdjNote('');
            await loadAdjustments(adjFacilityId);
        } catch (err) {
            setAdjError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function archiveAdjustment(adjId: number) {
        if (adjFacilityId === null) {
            return;
        }
        setAdjError('');
        try {
            await mutate(`/admin/fin-facilities/${segment}/${adjFacilityId}/adjustments/${adjId}/archive`, token, 'PATCH', {});
            await loadAdjustments(adjFacilityId);
        } catch (err) {
            setAdjError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function saveEnrichment(e: FormEvent) {
        e.preventDefault();
        if (!enrichTarget) {
            return;
        }
        setError('');
        try {
            await mutate(`/admin/fin-facilities/${segment}/overlay`, token, 'PATCH', {
                overlay_type: enrichTarget.overlay_type,
                overlay_ref: enrichTarget.overlay_ref,
                display_title: oTitle || null,
                admin_comment: oComment || null,
                opening_balance_delta: oOpeningDelta === '' ? null : Number(oOpeningDelta),
            });
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function submitOverlayAdjustment(e: FormEvent) {
        e.preventDefault();
        if (!adjOverlay || !nOverlayAdjDate || nOverlayAdjAmount === '') {
            return;
        }
        setOverlayAdjError('');
        try {
            await mutate(`/admin/fin-facilities/${segment}/overlay-adjustments`, token, 'POST', {
                overlay_type: adjOverlay.overlay_type,
                overlay_ref: adjOverlay.overlay_ref,
                effective_date: nOverlayAdjDate,
                amount: Number(nOverlayAdjAmount),
                note: nOverlayAdjNote || null,
            });
            setNOverlayAdjAmount('');
            setNOverlayAdjNote('');
            await loadOverlayAdjustments(adjOverlay);
        } catch (err) {
            setOverlayAdjError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function archiveOverlayAdjustment(adjId: number) {
        if (!adjOverlay) {
            return;
        }
        setOverlayAdjError('');
        try {
            await mutate(`/admin/fin-facilities/${segment}/overlay-adjustments/${adjId}/archive`, token, 'PATCH', {
                overlay_type: adjOverlay.overlay_type,
                overlay_ref: adjOverlay.overlay_ref,
            });
            await loadOverlayAdjustments(adjOverlay);
        } catch (err) {
            setOverlayAdjError(err instanceof Error ? err.message : 'Greška');
        }
    }

    const counterparties = payload?.counterparties ?? [];
    const fc = finFacilityFormCopy(adminSection, locale);

    const displayRows = useMemo(() => {
        let r = payload?.rows ?? [];
        if (companyFilter !== '') {
            const cid = Number(companyFilter);
            r = r.filter((row) => row.company_id === cid);
        }
        const q = tableSearch.trim();
        if (!q) {
            return r;
        }

        return r.filter((row) =>
            matchesQuickSearch(
                [
                    row.id,
                    row.company_code,
                    row.title,
                    row.reference_code,
                    row.counterparty_name,
                    row.principal_amount,
                    row.currency_code,
                    row.status,
                    row.notes,
                    row.start_date,
                    row.maturity_date,
                    row.docdoc_contract_url,
                    row.created_at,
                    row.updated_at,
                ],
                q,
            ),
        );
    }, [payload?.rows, tableSearch, companyFilter]);

    // Za modul 124 spajamo ručne (bi_loan_book_entries) i auto-detektirane (bi_loan_movements) u jednu listu.
    type Unified124Row =
        | { kind: 'manual'; row: FinFacilityRow }
        | { kind: 'auto'; row: LoanMovementGroupRow };
    const unifiedLoanBookRows = useMemo<Unified124Row[]>(() => {
        if (adminSection !== '124') return [];
        const cid = companyFilter !== '' ? Number(companyFilter) : null;
        const manualRows: Unified124Row[] = (payload?.rows ?? [])
            .filter((r) => {
                if (cid !== null && r.company_id !== cid) return false;
                if (!tableSearch.trim()) return true;
                return matchesQuickSearch([r.company_code, r.title, r.reference_code, r.counterparty_name, r.notes], tableSearch);
            })
            .map((row) => ({ kind: 'manual' as const, row }));
        const autoRows: Unified124Row[] = (payload?.loan_movement_groups ?? [])
            .filter((r) => {
                if (cid !== null && r.company_id !== cid) return false;
                if (!tableSearch.trim()) return true;
                return matchesQuickSearch([r.company_code, r.counterparty_name, r.display_title, r.admin_comment, r.net], tableSearch);
            })
            .map((row) => ({ kind: 'auto' as const, row }));
        return [...autoRows, ...manualRows];
    }, [adminSection, payload?.rows, payload?.loan_movement_groups, tableSearch, companyFilter]);

    const displayModule5Recons = useMemo(() => {
        const r = payload?.module5_reconciliations ?? [];
        const q = tableSearch.trim();
        if (!q) {
            return r;
        }
        return r.filter((row) =>
            matchesQuickSearch(
                [
                    row.id,
                    row.company_code,
                    row.counterparty_name,
                    row.account_code,
                    row.account_name,
                    row.metric,
                    row.system_note,
                    row.display_title,
                    row.admin_comment,
                ],
                q,
            ),
        );
    }, [payload?.module5_reconciliations, tableSearch]);

    const displayModule5Tb = useMemo(() => {
        const r = payload?.module5_tb_positions ?? [];
        const q = tableSearch.trim();
        if (!q) {
            return r;
        }
        return r.filter((row) =>
            matchesQuickSearch(
                [row.company_code, row.account_code, row.account_name, row.instrument, row.display_title, row.admin_comment],
                q,
            ),
        );
    }, [payload?.module5_tb_positions, tableSearch]);

    const displayBankAccounts = useMemo(() => {
        const r = payload?.bank_accounts ?? [];
        const q = tableSearch.trim();
        if (!q) {
            return r;
        }
        return r.filter((row) =>
            matchesQuickSearch(
                [row.company_code, row.iban, row.bank_name, row.account_label, row.display_title, row.admin_comment],
                q,
            ),
        );
    }, [payload?.bank_accounts, tableSearch]);

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🏦</div>
                <div className="bi-module-hero-text">
                    <h1>{title}</h1>
                    <p className="muted">
                        {locale === 'en'
                            ? 'Registers (phase F) plus module 5 reconciliation lines, TB finance openings, and (banks) giro accounts. Enrich with name, comment, opening delta; corrective lines apply in the loans module.'
                            : 'Registri (faza F) plus redovi usklađenja modula 5, početne TB financijske pozicije i (banke) žiro računi. Obogaćivanje: ime, komentar, delta početnog stanja; korektivni retci ulaze u modul pozajmica.'}
                    </p>
                </div>
            </div>

            {payload?.docdoc ? (
                <p className="muted" style={{ marginBottom: '0.75rem' }}>
                    DocDoc: {payload.docdoc.enabled ? payload.docdoc.base_url : locale === 'en' ? 'not configured' : 'nije konfigurirano'}
                    {' — '}
                    {payload.docdoc.note}
                </p>
            ) : null}

            <div className="admin-toolbar" style={{ marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                <span className="muted">{locale === 'en' ? 'Status' : 'Status'}:</span>
                {(['active', 'archived', 'all'] as const).map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={statusFilter === s ? 'compact' : 'secondary compact'}
                        onClick={() => setStatusFilter(s)}
                    >
                        {s}
                    </button>
                ))}
                <span className="muted" style={{ marginLeft: '0.75rem' }}>{locale === 'en' ? 'Company' : 'Tvrtka'}:</span>
                <select value={companyFilter} onChange={(ev) => setCompanyFilter(ev.target.value)}>
                    <option value="">{locale === 'en' ? 'All companies' : 'Sve tvrtke'}</option>
                    {payload?.companies?.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.code}
                        </option>
                    ))}
                </select>
                <div style={{ flex: '1 1 14rem', minWidth: '12rem', maxWidth: '22rem', marginLeft: 'auto' }}>
                    <QuickSearchInput
                        value={tableSearch}
                        onChange={setTableSearch}
                        placeholder={locale === 'en' ? 'Search table…' : 'Pretraga tablice…'}
                    />
                </div>
            </div>

            {loading ? <p className="muted">{locale === 'en' ? 'Loading…' : 'Učitavam…'}</p> : null}
            {error ? <p className="error">{error}</p> : null}

            {!loading && payload ? (
                <>
                    <AdminCreateCard title={fc.newTitle} hint={fc.newHint} locale={locale}>
                    <form onSubmit={submitNew}>
                        <div style={{ marginBottom: 0 }}>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
                                    gap: '0.75rem',
                                    maxWidth: '58rem',
                                }}
                            >
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Company' : 'Tvrtka'}
                                    <select value={nCompany} onChange={(ev) => setNCompany(ev.target.value)} required>
                                        <option value="">{locale === 'en' ? 'Select' : 'Odaberi'}</option>
                                        {payload.companies.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.code} — {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Counterparty' : 'Protustranka'}
                                    <select value={nCounterparty} onChange={(ev) => setNCounterparty(ev.target.value)}>
                                        <option value="">{locale === 'en' ? 'None' : '—'}</option>
                                        {counterparties.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Title' : 'Naslov'}
                                    <input value={nTitle} onChange={(ev) => setNTitle(ev.target.value)} required />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Reference' : 'Referenca'}
                                    <input value={nRef} onChange={(ev) => setNRef(ev.target.value)} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Principal' : 'Glavnica'}
                                    <input type="number" step="0.01" value={nPrincipal} onChange={(ev) => setNPrincipal(ev.target.value)} required />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Currency' : 'Valuta'}
                                    <input value={nCurrency} maxLength={3} onChange={(ev) => setNCurrency(ev.target.value.toUpperCase())} />
                                </label>
                                {adminSection === '123' || adminSection === '122' ? (
                                    <label className="filter-label" style={{ margin: 0 }}>
                                        {locale === 'en' ? 'Monthly installment (optional)' : 'Mjesečna rata (opc.)'}
                                        <input type="number" step="0.01" value={nMonthly} onChange={(ev) => setNMonthly(ev.target.value)} />
                                    </label>
                                ) : null}
                                {adminSection === '124' ? (
                                    <label className="filter-label" style={{ margin: 0 }}>
                                        {locale === 'en' ? 'Annual interest % (nominal)' : 'Godišnja kamata % (nominalna)'}
                                        <input type="number" step="0.0001" value={nAnnualPct} onChange={(ev) => setNAnnualPct(ev.target.value)} />
                                    </label>
                                ) : null}
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Start' : 'Početak'}
                                    <DateInput value={nStart} onChange={setNStart} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Maturity' : 'Dospijeće'}
                                    <DateInput value={nMaturity} onChange={setNMaturity} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    DocDoc URL
                                    <input value={nDocdoc} onChange={(ev) => setNDocdoc(ev.target.value)} placeholder="https://…" />
                                </label>
                                <label className="filter-label" style={{ margin: 0, gridColumn: '1 / -1' }}>
                                    {locale === 'en' ? 'Notes' : 'Napomene'}
                                    <input value={nNotes} onChange={(ev) => setNNotes(ev.target.value)} />
                                </label>
                            </div>
                            <div className="admin-toolbar" style={{ marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <button className="compact" type="submit">
                                    {locale === 'en' ? 'Create' : 'Kreiraj'}
                                </button>
                            </div>
                        </div>
                    </form>
                    </AdminCreateCard>

                    {adminSection === '124' ? (
                        <div className="table-wrap" style={{ marginTop: '1rem' }}>
                            <table className="admin-user-table">
                                <thead>
                                    <tr>
                                        <th>{locale === 'en' ? 'Source' : 'Izvor'}</th>
                                        <th>{locale === 'en' ? 'Co.' : 'Tvrtka'}</th>
                                        <th>{locale === 'en' ? 'Counterparty' : 'Protustranka'}</th>
                                        <th title={locale === 'en' ? 'Opening (PD-IPO previous year)' : 'Početno stanje (PD-IPO prethodne godine, glavnica+kamate)'}>{locale === 'en' ? 'Opening' : 'Početno'}</th>
                                        <th>{locale === 'en' ? 'Movements' : 'Stavki'}</th>
                                        <th>{locale === 'en' ? 'Inflow' : 'Priliv'}</th>
                                        <th>{locale === 'en' ? 'Outflow' : 'Odliv'}</th>
                                        <th>{locale === 'en' ? 'Net change' : 'Neto promjena'}</th>
                                        <th title={locale === 'en' ? 'Opening + Net' : 'Početno + neto = trenutno stanje'}>{locale === 'en' ? 'Current' : 'Trenutno'}</th>
                                        <th>{locale === 'en' ? 'From' : 'Od'}</th>
                                        <th>{locale === 'en' ? 'To' : 'Do'}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {unifiedLoanBookRows.map((item) => {
                                        if (item.kind === 'auto') {
                                            const r = item.row;
                                            const hasOpening = r.opening_total !== 0;
                                            const hasMovements = r.n > 0;
                                            const sourceLabel = hasOpening && hasMovements ? (locale === 'en' ? 'PD-IPO + bank' : 'PD-IPO + izvodi') : hasOpening ? 'PD-IPO' : (locale === 'en' ? 'bank' : 'izvodi');
                                            return (
                                                <tr key={`auto-${r.overlay_ref}`}>
                                                    <td><span className="pill" style={{ background: 'rgba(var(--ad-acc-rgb),0.18)', borderRadius: 999, padding: '2px 8px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{sourceLabel}</span></td>
                                                    <td>{r.company_code}</td>
                                                    <td>
                                                        <strong>{r.display_title ?? r.counterparty_name ?? '—'}</strong>
                                                        {r.admin_comment ? <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>{r.admin_comment}</p> : null}
                                                        {r.recon_notes.length > 0 ? <p className="muted" style={{ margin: 0, fontSize: '0.75rem' }}>{r.recon_notes.join(' · ')}</p> : null}
                                                    </td>
                                                    <td title={r.opening_principal !== 0 || r.opening_interest !== 0 ? `${locale === 'en' ? 'principal' : 'glavnica'} ${formatMoney(r.opening_principal, locale)} · ${locale === 'en' ? 'interest' : 'kamate'} ${formatMoney(r.opening_interest, locale)}` : undefined}>
                                                        {r.opening_total !== 0 ? formatMoney(r.opening_total, locale) : '—'}
                                                    </td>
                                                    <td>{r.n || '—'}</td>
                                                    <td>{r.inflow ? formatMoney(r.inflow, locale) : '—'}</td>
                                                    <td>{r.outflow ? formatMoney(r.outflow, locale) : '—'}</td>
                                                    <td>{r.n > 0 ? formatMoney(r.net, locale) : '—'}</td>
                                                    <td><strong>{formatMoney(r.current_balance, locale)}</strong></td>
                                                    <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{r.first_movement ?? '—'}</td>
                                                    <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{r.last_movement ?? '—'}</td>
                                                    <td>
                                                        {(() => {
                                                            const keyStr = `auto-${r.overlay_type}-${r.overlay_ref}`;
                                                            return (
                                                                <div className="row-actions-menu" style={{ position: 'relative', display: 'inline-block' }}>
                                                                    <button type="button" className={openActionMenuId === keyStr ? 'compact' : 'secondary compact'} onClick={() => setOpenActionMenuId((cur) => cur === keyStr ? null : keyStr)}>
                                                                        {locale === 'en' ? 'Actions ▾' : 'Akcije ▾'}
                                                                    </button>
                                                                    {openActionMenuId === keyStr ? (
                                                                        <div role="menu" className="row-actions-dropdown" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--surface, #fff)', border: '1px solid rgba(var(--ad-soft-rgb),0.32)', borderRadius: 10, boxShadow: '0 8px 20px rgba(0,0,0,0.12)', padding: 4, zIndex: 20, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                            <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); setAdjFacilityId(null); setAdjOverlay(null); setEnrichTarget({ overlay_type: r.overlay_type, overlay_ref: r.overlay_ref }); }}>
                                                                                {locale === 'en' ? 'Enrich' : 'Obogati'}
                                                                            </button>
                                                                            <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => {
                                                                                setOpenActionMenuId(null);
                                                                                setAdjFacilityId(null);
                                                                                setEnrichTarget(null);
                                                                                setAdjOverlay((cur) => cur?.overlay_ref === r.overlay_ref && cur.overlay_type === r.overlay_type ? null : { overlay_type: r.overlay_type, overlay_ref: r.overlay_ref });
                                                                            }}>
                                                                                {locale === 'en' ? 'Corrections' : 'Korekcije'}
                                                                            </button>
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                </tr>
                                            );
                                        }
                                        const m = item.row;
                                        return (
                                            <tr key={`manual-${m.id}`}>
                                                <td><span className="pill" style={{ background: 'rgba(0,0,0,0.06)', borderRadius: 999, padding: '2px 8px', fontSize: '0.75rem' }}>{locale === 'en' ? 'manual' : 'ručno'}</span></td>
                                                <td>{m.company_code}</td>
                                                <td>
                                                    <strong>{m.title}</strong>
                                                    {m.counterparty_name ? <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>{m.counterparty_name}</p> : null}
                                                </td>
                                                <td>—</td>
                                                <td>—</td>
                                                <td>—</td>
                                                <td>—</td>
                                                <td>—</td>
                                                <td><strong>{formatMoney(m.principal_amount, locale)}</strong></td>
                                                <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{m.start_date ? formatDate(m.start_date, locale) : '—'}</td>
                                                <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{m.maturity_date ? formatDate(m.maturity_date, locale) : '—'}</td>
                                                <td>
                                                    <div className="row-actions-menu" style={{ position: 'relative', display: 'inline-block' }}>
                                                        <button type="button" className={openActionMenuId === m.id ? 'compact' : 'secondary compact'} onClick={() => setOpenActionMenuId((cur) => cur === m.id ? null : m.id)}>{locale === 'en' ? 'Actions ▾' : 'Akcije ▾'}</button>
                                                        {openActionMenuId === m.id ? (
                                                            <div role="menu" className="row-actions-dropdown" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--surface, #fff)', border: '1px solid rgba(var(--ad-soft-rgb),0.32)', borderRadius: 10, boxShadow: '0 8px 20px rgba(0,0,0,0.12)', padding: 4, zIndex: 20, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); startEdit(m); }}>{locale === 'en' ? 'Edit' : 'Uredi'}</button>
                                                                <button type="button" className="secondary compact" disabled={m.status === 'archived'} style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); void archiveRow(m.id); }}>{locale === 'en' ? 'Archive' : 'Arhiviraj'}</button>
                                                                <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); void deleteRow(m.id); }}>{locale === 'en' ? 'Delete' : 'Obriši'}</button>
                                                                <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); void loadSchedule(m.id); }}>{locale === 'en' ? 'Schedule' : 'Raspored'}</button>
                                                                <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); setAdjOverlay(null); setEnrichTarget(null); setAdjFacilityId((cur) => cur === m.id ? null : m.id); }}>{locale === 'en' ? 'Corrections' : 'Korekcije'}</button>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {unifiedLoanBookRows.length === 0 ? <p className="muted">{locale === 'en' ? 'No rows.' : 'Nema redaka.'}</p> : null}
                        </div>
                    ) : (
                    <div className="table-wrap" style={{ marginTop: '1rem' }}>
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{locale === 'en' ? 'Co.' : 'Tvrtka'}</th>
                                    <th>{locale === 'en' ? 'Title' : 'Naslov'}</th>
                                    <th>{locale === 'en' ? 'Principal' : 'Glavnica'}</th>
                                    {adminSection === '122' || adminSection === '123' ? (
                                        <th>{locale === 'en' ? 'Latest balance' : 'Najnovije stanje'}</th>
                                    ) : null}
                                    <th>{locale === 'en' ? 'Start' : 'Početak'}</th>
                                    <th>{locale === 'en' ? 'Mat.' : 'Dospijeće'}</th>
                                    {adminSection === '122' || adminSection === '123' ? (
                                        <th>{locale === 'en' ? 'Monthly' : 'Mjesečno'}</th>
                                    ) : null}
                                    <th>{locale === 'en' ? 'Cre.' : 'Kreir.'}</th>
                                    <th>{locale === 'en' ? 'Upd.' : 'Ažur.'}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {displayRows.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.id}</td>
                                        <td>{row.company_code}</td>
                                        <td>{row.title}</td>
                                        <td>{formatMoney(row.principal_amount, locale)}</td>
                                        {adminSection === '122' || adminSection === '123' ? (
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <strong>{row.current_balance != null ? formatMoney(row.current_balance, locale) : '—'}</strong>
                                            </td>
                                        ) : null}
                                        <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                            {row.start_date ? formatDate(row.start_date, locale) : '—'}
                                        </td>
                                        <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                            {row.maturity_date ? formatDate(row.maturity_date, locale) : '—'}
                                        </td>
                                        {adminSection === '122' || adminSection === '123' ? (
                                            <td>
                                                {row.monthly_installment != null ? formatMoney(row.monthly_installment, locale) : '—'}
                                            </td>
                                        ) : null}
                                        <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                            {row.created_at ? formatDateTime(row.created_at, locale) : '—'}
                                        </td>
                                        <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                            {row.updated_at ? formatDateTime(row.updated_at, locale) : '—'}
                                        </td>
                                        <td>
                                            <div className="row-actions-menu" style={{ position: 'relative', display: 'inline-block' }}>
                                                <button
                                                    type="button"
                                                    className={openActionMenuId === row.id ? 'compact' : 'secondary compact'}
                                                    onClick={() => setOpenActionMenuId((cur) => (cur === row.id ? null : row.id))}
                                                    aria-haspopup="menu"
                                                    aria-expanded={openActionMenuId === row.id}
                                                >
                                                    {locale === 'en' ? 'Actions ▾' : 'Akcije ▾'}
                                                </button>
                                                {openActionMenuId === row.id ? (
                                                    <div role="menu" className="row-actions-dropdown" style={{
                                                        position: 'absolute', right: 0, top: '100%', marginTop: 4,
                                                        background: 'var(--surface, #fff)',
                                                        border: '1px solid rgba(var(--ad-soft-rgb),0.32)',
                                                        borderRadius: 10, boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                                                        padding: 4, zIndex: 20, minWidth: 160,
                                                        display: 'flex', flexDirection: 'column', gap: 2,
                                                    }}>
                                                        <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); startEdit(row); }}>
                                                            {locale === 'en' ? 'Edit' : 'Uredi'}
                                                        </button>
                                                        <button type="button" className="secondary compact" disabled={row.status === 'archived'} style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); void archiveRow(row.id); }}>
                                                            {locale === 'en' ? 'Archive' : 'Arhiviraj'}
                                                        </button>
                                                        <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); void deleteRow(row.id); }}>
                                                            {locale === 'en' ? 'Delete' : 'Obriši'}
                                                        </button>
                                                        <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); void loadSchedule(row.id); }}>
                                                            {locale === 'en' ? 'Schedule' : 'Raspored'}
                                                        </button>
                                                        {adminSection === '122' || adminSection === '123' ? (
                                                            <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => {
                                                                setOpenActionMenuId(null);
                                                                setAdjFacilityId(null);
                                                                setAdjOverlay(null);
                                                                setEnrichTarget({
                                                                    overlay_type: adminSection === '122' ? 'bank_credit' : 'leasing_contract',
                                                                    overlay_ref: String(row.id),
                                                                });
                                                            }}>
                                                                {locale === 'en' ? 'Enrich' : 'Obogati'}
                                                            </button>
                                                        ) : null}
                                                        <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => {
                                                            setOpenActionMenuId(null);
                                                            setAdjOverlay(null);
                                                            setEnrichTarget(null);
                                                            setAdjFacilityId((cur) => (cur === row.id ? null : row.id));
                                                        }}>
                                                            {locale === 'en' ? 'Corrections' : 'Korekcije'}
                                                        </button>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    )}

                    {enrichTarget ? (
                        <form className="admin-section-card" style={{ marginTop: '1rem' }} onSubmit={saveEnrichment}>
                            <h4>
                                {locale === 'en'
                                    ? `Enrich (${enrichTarget.overlay_type} · ${enrichTarget.overlay_ref})`
                                    : `Obogaćivanje (${enrichTarget.overlay_type} · ${enrichTarget.overlay_ref})`}
                            </h4>
                            <label className="filter-label">
                                {locale === 'en' ? 'Display name' : 'Prikazno ime'}
                                <input value={oTitle} onChange={(ev) => setOTitle(ev.target.value)} />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Admin comment' : 'Komentar'}
                                <input value={oComment} onChange={(ev) => setOComment(ev.target.value)} />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Opening balance delta (loans module)' : 'Delta početnog stanja (modul pozajmica)'}
                                <input type="number" step="0.0001" value={oOpeningDelta} onChange={(ev) => setOOpeningDelta(ev.target.value)} />
                            </label>
                            <div className="admin-toolbar">
                                <button className="compact" type="submit">
                                    {locale === 'en' ? 'Save overlay' : 'Spremi overlay'}
                                </button>
                                <button type="button" className="secondary compact" onClick={() => setEnrichTarget(null)}>
                                    {locale === 'en' ? 'Close' : 'Zatvori'}
                                </button>
                            </div>
                        </form>
                    ) : null}

                    {adminSection === '124' || displayModule5Recons.length === 0 ? null : (
                    <div className="table-wrap" style={{ marginTop: '1.25rem' }}>
                        <h4 className="muted" style={{ marginBottom: '0.5rem' }}>
                            {locale === 'en' ? 'Module 5 — PD-IPO vs trial balance rows' : 'Modul 5 — redovi PD-IPO vs bruto bilanca'}
                        </h4>
                        {displayModule5Recons.length === 0 ? null : (
                            <table className="admin-user-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>{locale === 'en' ? 'Co.' : 'Tvrtka'}</th>
                                        <th>{locale === 'en' ? 'Counterparty' : 'Protustranka'}</th>
                                        <th>{locale === 'en' ? 'Account' : 'Konto'}</th>
                                        <th>{locale === 'en' ? 'Metric' : 'Metrika'}</th>
                                        <th>BB</th>
                                        <th>{locale === 'en' ? 'Name / note' : 'Ime / napomena'}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayModule5Recons.map((row) => (
                                        <tr key={`m5-${row.id}`}>
                                            <td>{row.id}</td>
                                            <td>{row.company_code}</td>
                                            <td>{row.counterparty_name ?? '—'}</td>
                                            <td className="muted">{row.account_code ?? '—'}</td>
                                            <td>{row.metric}</td>
                                            <td>{formatMoney(row.trial_balance_amount, locale)}</td>
                                            <td style={{ maxWidth: '14rem' }} className="muted">
                                                {row.display_title ?? row.system_note ?? '—'}
                                            </td>
                                            <td className="admin-toolbar">
                                                <button
                                                    type="button"
                                                    className="secondary compact"
                                                    onClick={() => {
                                                        setAdjFacilityId(null);
                                                        setEnrichTarget({ overlay_type: row.overlay_type, overlay_ref: row.overlay_ref });
                                                    }}
                                                >
                                                    {locale === 'en' ? 'Enrich' : 'Obogati'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={
                                                        adjOverlay?.overlay_ref === row.overlay_ref && adjOverlay?.overlay_type === row.overlay_type
                                                            ? 'compact'
                                                            : 'secondary compact'
                                                    }
                                                    onClick={() => {
                                                        setAdjFacilityId(null);
                                                        setEnrichTarget(null);
                                                        setAdjOverlay((cur) =>
                                                            cur?.overlay_ref === row.overlay_ref && cur.overlay_type === row.overlay_type
                                                                ? null
                                                                : { overlay_type: row.overlay_type, overlay_ref: row.overlay_ref },
                                                        );
                                                    }}
                                                >
                                                    {locale === 'en' ? 'Corrections' : 'Korekcije'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    )}

                    {(adminSection === '122' || adminSection === '123') && displayModule5Tb.length > 0 ? (
                        <div className="table-wrap" style={{ marginTop: '1.25rem' }}>
                            <h4 className="muted" style={{ marginBottom: '0.5rem' }}>
                                {locale === 'en' ? 'Module 5 — trial balance finance (no bank movements)' : 'Modul 5 — TB financijski leasing / banka (bez izvoda)'}
                            </h4>
                            {displayModule5Tb.length === 0 ? null : (
                                <table className="admin-user-table">
                                    <thead>
                                        <tr>
                                            <th>{locale === 'en' ? 'Co.' : 'Tvrtka'}</th>
                                            <th>{locale === 'en' ? 'Account' : 'Konto'}</th>
                                            <th>{locale === 'en' ? 'Opening' : 'Početno'}</th>
                                            <th>{locale === 'en' ? 'Kind' : 'Vrsta'}</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayModule5Tb.map((row) => (
                                            <tr key={row.overlay_ref}>
                                                <td>{row.company_code}</td>
                                                <td className="muted">
                                                    {row.account_code} · {row.account_name}
                                                </td>
                                                <td>{formatMoney(row.signed_opening, locale)}</td>
                                                <td>{row.instrument}</td>
                                                <td className="admin-toolbar">
                                                    <button
                                                        type="button"
                                                        className="secondary compact"
                                                        onClick={() => {
                                                            setAdjFacilityId(null);
                                                            setEnrichTarget({ overlay_type: row.overlay_type, overlay_ref: row.overlay_ref });
                                                        }}
                                                    >
                                                        {locale === 'en' ? 'Enrich' : 'Obogati'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={
                                                            adjOverlay?.overlay_ref === row.overlay_ref && adjOverlay?.overlay_type === row.overlay_type
                                                                ? 'compact'
                                                                : 'secondary compact'
                                                        }
                                                        onClick={() => {
                                                            setAdjFacilityId(null);
                                                            setEnrichTarget(null);
                                                            setAdjOverlay((cur) =>
                                                                cur?.overlay_ref === row.overlay_ref && cur.overlay_type === row.overlay_type
                                                                    ? null
                                                                    : { overlay_type: row.overlay_type, overlay_ref: row.overlay_ref },
                                                            );
                                                        }}
                                                    >
                                                        {locale === 'en' ? 'Corrections' : 'Korekcije'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}

                    {adminSection === '122' ? (
                        <div className="table-wrap" style={{ marginTop: '1.25rem' }}>
                            <h4 className="muted" style={{ marginBottom: '0.5rem' }}>
                                {locale === 'en' ? 'Giro accounts (cashflow imports)' : 'Žiro računi (uvoz izvoda)'}
                            </h4>
                            {displayBankAccounts.length === 0 ? (
                                <p className="muted">{locale === 'en' ? 'No bank accounts.' : 'Nema žiro računa.'}</p>
                            ) : (
                                <table className="admin-user-table">
                                    <thead>
                                        <tr>
                                            <th>{locale === 'en' ? 'Co.' : 'Tvrtka'}</th>
                                            <th>IBAN · {locale === 'en' ? 'Bank' : 'Banka'}</th>
                                            <th title={locale === 'en' ? 'Opening balance (start of fiscal year)' : 'Početno stanje (početak fiskalne godine)'}>{locale === 'en' ? 'Opening' : 'Početno'}</th>
                                            <th>{locale === 'en' ? 'Stmt n' : 'Stavki'}</th>
                                            <th>{locale === 'en' ? 'Net change' : 'Neto promjena'}</th>
                                            <th title={locale === 'en' ? 'Opening + net = current balance' : 'Početno + neto promjena = trenutno stanje'}>{locale === 'en' ? 'Current' : 'Trenutno'}</th>
                                            <th>{locale === 'en' ? 'Last' : 'Zadnji'}</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayBankAccounts.map((row) => {
                                            const acctRowKey = -row.id; // negativni key da se ne sudara s facility id-evima
                                            return (
                                            <tr key={row.id}>
                                                <td>{row.company_code}</td>
                                                <td>
                                                    <strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }}>{row.iban}</strong>
                                                    {row.bank_name ? <p className="muted" style={{ margin: 0, fontSize: '0.75rem' }}>{row.bank_name}</p> : null}
                                                </td>
                                                <td title={row.opening_source ? `source: ${row.opening_source}${row.opening_date ? ' · '+row.opening_date : ''}` : undefined}>
                                                    {row.opening_balance != null ? formatMoney(row.opening_balance, locale) : '—'}
                                                </td>
                                                <td>{row.movements_n ?? 0}</td>
                                                <td>{row.movements_net != null ? formatMoney(row.movements_net, locale) : '—'}</td>
                                                <td><strong>{row.current_balance != null ? formatMoney(row.current_balance, locale) : '—'}</strong></td>
                                                <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{row.last_movement_date ?? '—'}</td>
                                                <td>
                                                    <div className="row-actions-menu" style={{ position: 'relative', display: 'inline-block' }}>
                                                        <button type="button" className={openActionMenuId === acctRowKey ? 'compact' : 'secondary compact'} onClick={() => setOpenActionMenuId((cur) => cur === acctRowKey ? null : acctRowKey)}>
                                                            {locale === 'en' ? 'Actions ▾' : 'Akcije ▾'}
                                                        </button>
                                                        {openActionMenuId === acctRowKey ? (
                                                            <div role="menu" className="row-actions-dropdown" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--surface, #fff)', border: '1px solid rgba(var(--ad-soft-rgb),0.32)', borderRadius: 10, boxShadow: '0 8px 20px rgba(0,0,0,0.12)', padding: 4, zIndex: 20, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => { setOpenActionMenuId(null); setAdjFacilityId(null); setEnrichTarget({ overlay_type: row.overlay_type, overlay_ref: row.overlay_ref }); }}>
                                                                    {locale === 'en' ? 'Enrich' : 'Obogati'}
                                                                </button>
                                                                <button type="button" className="secondary compact" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => {
                                                                    setOpenActionMenuId(null);
                                                                    setAdjFacilityId(null);
                                                                    setEnrichTarget(null);
                                                                    setAdjOverlay((cur) =>
                                                                        cur?.overlay_ref === row.overlay_ref && cur.overlay_type === row.overlay_type
                                                                            ? null
                                                                            : { overlay_type: row.overlay_type, overlay_ref: row.overlay_ref },
                                                                    );
                                                                }}>
                                                                    {locale === 'en' ? 'Corrections' : 'Korekcije'}
                                                                </button>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}

                    {adjOverlay !== null ? (
                        <section className="admin-section-card" style={{ marginTop: '1rem' }}>
                            <h4>
                                {locale === 'en'
                                    ? `Overlay corrections (${adjOverlay.overlay_type} · ${adjOverlay.overlay_ref})`
                                    : `Korekcije overlaya (${adjOverlay.overlay_type} · ${adjOverlay.overlay_ref})`}
                            </h4>
                            <p className="muted" style={{ marginBottom: '0.75rem' }}>
                                {locale === 'en'
                                    ? 'Applied in the loans module (same sign rules as manual corrections).'
                                    : 'Primjenjuju se u modulu pozajmica (isti predznak kao ručne korekcije).'}
                            </p>
                            {overlayAdjLoading ? <p className="muted">…</p> : null}
                            {overlayAdjError ? <p className="error">{overlayAdjError}</p> : null}
                            <form className="admin-form-grid" onSubmit={submitOverlayAdjustment}>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Effective date' : 'Datum učinka'}
                                    <DateInput value={nOverlayAdjDate} onChange={setNOverlayAdjDate} required />
                                </label>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Amount' : 'Iznos'}
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={nOverlayAdjAmount}
                                        onChange={(ev) => setNOverlayAdjAmount(ev.target.value)}
                                        required
                                    />
                                </label>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Note' : 'Napomena'}
                                    <input value={nOverlayAdjNote} onChange={(ev) => setNOverlayAdjNote(ev.target.value)} />
                                </label>
                                <button className="compact" type="submit">
                                    {locale === 'en' ? 'Add' : 'Dodaj'}
                                </button>
                            </form>
                            <div className="table-wrap">
                                <table className="admin-user-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>{locale === 'en' ? 'Date' : 'Datum'}</th>
                                            <th>{locale === 'en' ? 'Amount' : 'Iznos'}</th>
                                            <th>{locale === 'en' ? 'Status' : 'Status'}</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overlayAdjRows.map((a) => (
                                            <tr key={a.id}>
                                                <td>{a.id}</td>
                                                <td>{formatDate(a.effective_date, locale)}</td>
                                                <td>{formatMoney(a.amount, locale)}</td>
                                                <td>{a.status}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="secondary compact"
                                                        disabled={a.status === 'archived'}
                                                        onClick={() => void archiveOverlayAdjustment(a.id)}
                                                    >
                                                        {locale === 'en' ? 'Archive' : 'Arhiviraj'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button type="button" className="secondary compact" onClick={() => setAdjOverlay(null)}>
                                {locale === 'en' ? 'Close panel' : 'Zatvori panel'}
                            </button>
                        </section>
                    ) : null}

                    {scheduleForId !== null ? (
                        <section className="admin-section-card" style={{ marginTop: '1rem' }}>
                            <div className="module-heading compact-heading">
                                <h4>
                                    {locale === 'en' ? `Repayment schedule #${scheduleForId}` : `Raspored otplate #${scheduleForId}`}
                                </h4>
                                <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() => {
                                        setScheduleForId(null);
                                        setScheduleRows([]);
                                    }}
                                >
                                    {locale === 'en' ? 'Close' : 'Zatvori'}
                                </button>
                            </div>
                            {scheduleLoading ? <p className="muted">…</p> : null}
                            {!scheduleLoading && scheduleRows.length === 0 ? (
                                <p className="muted">
                                    {locale === 'en'
                                        ? 'No schedule lines (add dates / installment / rate).'
                                        : 'Nema redaka rasporeda (dodaj datume, ratu ili kamatu).'}
                                </p>
                            ) : null}
                            {!scheduleLoading && scheduleRows.length > 0 ? (
                                <div className="table-wrap">
                                    <table className="admin-user-table">
                                        <thead>
                                            <tr>
                                                <th>{locale === 'en' ? 'Due' : 'Dospijeće'}</th>
                                                <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Amount' : 'Iznos'}</th>
                                                <th>{locale === 'en' ? 'Kind' : 'Vrsta'}</th>
                                                <th>{locale === 'en' ? 'Label' : 'Opis'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scheduleRows.slice(0, 120).map((r) => (
                                                <tr key={`${r.due_date}-${r.kind}-${r.label}`}>
                                                    <td>{formatDate(r.due_date, locale)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatMoney(r.amount, locale)}</td>
                                                    <td className="muted">{r.kind}</td>
                                                    <td>{r.label}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </section>
                    ) : null}

                    {editId ? (
                        <form className="admin-section-card" style={{ marginTop: '1rem' }} onSubmit={saveEdit}>
                            <h4>{fc.editTitle(editId)}</h4>
                            <label className="filter-label">
                                {locale === 'en' ? 'Company' : 'Tvrtka'}
                                <select value={eCompany} onChange={(ev) => setECompany(ev.target.value)} required>
                                    {payload.companies.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.code}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Counterparty' : 'Protustranka'}
                                <select value={eCounterparty} onChange={(ev) => setECounterparty(ev.target.value)}>
                                    <option value="">{locale === 'en' ? 'None' : '—'}</option>
                                    {counterparties.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Title' : 'Naslov'}
                                <input value={eTitle} onChange={(ev) => setETitle(ev.target.value)} required />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Reference' : 'Referenca'}
                                <input value={eRef} onChange={(ev) => setERef(ev.target.value)} />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Principal' : 'Glavnica'}
                                <input type="number" step="0.01" value={ePrincipal} onChange={(ev) => setEPrincipal(ev.target.value)} required />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Currency' : 'Valuta'}
                                <input value={eCurrency} maxLength={3} onChange={(ev) => setECurrency(ev.target.value.toUpperCase())} />
                            </label>
                            {adminSection === '123' || adminSection === '122' ? (
                                <label className="filter-label">
                                    {locale === 'en' ? 'Monthly installment' : 'Mjesečna rata'}
                                    <input type="number" step="0.01" value={eMonthly} onChange={(ev) => setEMonthly(ev.target.value)} />
                                </label>
                            ) : null}
                            {adminSection === '124' ? (
                                <label className="filter-label">
                                    {locale === 'en' ? 'Annual interest %' : 'Godišnja kamata %'}
                                    <input type="number" step="0.0001" value={eAnnualPct} onChange={(ev) => setEAnnualPct(ev.target.value)} />
                                </label>
                            ) : null}
                            <label className="filter-label">
                                {locale === 'en' ? 'Start' : 'Početak'}
                                <DateInput value={eStart} onChange={setEStart} />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Maturity' : 'Dospijeće'}
                                <DateInput value={eMaturity} onChange={setEMaturity} />
                            </label>
                            <label className="filter-label">
                                DocDoc URL
                                <input value={eDocdoc} onChange={(ev) => setEDocdoc(ev.target.value)} />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Notes' : 'Napomene'}
                                <input value={eNotes} onChange={(ev) => setENotes(ev.target.value)} />
                            </label>
                            <div className="admin-toolbar">
                                <button className="compact" type="submit">
                                    {locale === 'en' ? 'Save' : 'Spremi'}
                                </button>
                                <button className="secondary compact" type="button" onClick={() => setEditId(null)}>
                                    {locale === 'en' ? 'Cancel' : 'Odustani'}
                                </button>
                            </div>
                        </form>
                    ) : null}

                    {adjFacilityId !== null ? (
                        <section className="admin-section-card" style={{ marginTop: '1rem' }}>
                            <h4>
                                {locale === 'en' ? `Corrections for #${adjFacilityId}` : `Korekcije za #${adjFacilityId}`}
                            </h4>
                            {adjLoading ? <p className="muted">…</p> : null}
                            {adjError ? <p className="error">{adjError}</p> : null}
                            <form className="admin-form-grid" onSubmit={submitAdjustment}>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Effective date' : 'Datum učinka'}
                                    <DateInput value={nAdjDate} onChange={setNAdjDate} required />
                                </label>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Amount' : 'Iznos'}
                                    <input type="number" step="0.01" value={nAdjAmount} onChange={(ev) => setNAdjAmount(ev.target.value)} required />
                                </label>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Note' : 'Napomena'}
                                    <input value={nAdjNote} onChange={(ev) => setNAdjNote(ev.target.value)} />
                                </label>
                                <button className="compact" type="submit">
                                    {locale === 'en' ? 'Add' : 'Dodaj'}
                                </button>
                            </form>
                            <div className="table-wrap">
                                <table className="admin-user-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>{locale === 'en' ? 'Date' : 'Datum'}</th>
                                            <th>{locale === 'en' ? 'Amount' : 'Iznos'}</th>
                                            <th>{locale === 'en' ? 'Status' : 'Status'}</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {adjRows.map((a) => (
                                            <tr key={a.id}>
                                                <td>{a.id}</td>
                                                <td>{formatDate(a.effective_date, locale)}</td>
                                                <td>{formatMoney(a.amount, locale)}</td>
                                                <td>{a.status}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="secondary compact"
                                                        disabled={a.status === 'archived'}
                                                        onClick={() => void archiveAdjustment(a.id)}
                                                    >
                                                        {locale === 'en' ? 'Archive' : 'Arhiviraj'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ) : null}
                </>
            ) : null}
        </section>
    );
}
